/**
 * Inventory Management Helper Functions
 * Handles stock checking, cart quantity validation, and available quantity calculations
 */

/**
 * Check if a variant size is out of stock
 * @param {Object} variantSize - The variant size object from API
 * @returns {boolean} - True if out of stock
 */
export const isVariantSizeOutOfStock = (variantSize) => {
  if (!variantSize) return true;
  return variantSize.quantity <= 0;
};

/**
 * Check if a variant (without sizes) is out of stock
 * @param {Object} variant - The variant object from API
 * @returns {boolean} - True if out of stock
 */
export const isVariantOutOfStock = (variant) => {
  if (!variant) return true;
  
  // For variants without sizes, check available_quantity
  if (!variant.product_variant_sizes || variant.product_variant_sizes.length === 0) {
    return variant.available_quantity <= 0;
  }
  
  // For variants with sizes, check if all sizes are out of stock
  return variant.product_variant_sizes.every(size => size.quantity <= 0);
};

/**
 * ✅ FIX: Get total quantity in cart for a variant size ACROSS ALL PLATING OPTIONS
 * This is crucial because plating is cosmetic - it doesn't create separate inventory
 * @param {Array} cartProducts - Products from Redux cart
 * @param {string} productId - Product ID
 * @param {string} variationId - Variation ID
 * @param {string} variantSizeId - Variant Size ID
 * @returns {number} - Total quantity in cart across all plating options
 */
export const getCartQuantityForVariantSizeAllPlatings = (
  cartProducts,
  productId,
  variationId,
  variantSizeId
) => {
  if (!cartProducts || !productId) return 0;

  let totalQuantity = 0;

  // Find all cart items matching this product and variation
  const matchingCartItems = cartProducts.filter(
    p => p.product_id === productId &&
         (p.product_variation_id ?? null) === (variationId ?? null)
  );

  matchingCartItems.forEach(cartProduct => {
    // Check wholesaleSelections (for products added with multiple sizes)
    if (cartProduct.wholesaleSelections && variantSizeId) {
      cartProduct.wholesaleSelections.forEach(selection => {
        // ✅ Match variant_size_id regardless of plating_id
        if (selection.variantSizeId === variantSizeId) {
          totalQuantity += (selection.qty || 0);
        }
      });
    }

    // For products without wholesaleSelections (single size products)
    if (cartProduct.variant_size_id === variantSizeId) {
      // ✅ Count this item regardless of plating_id
      totalQuantity += (cartProduct.quantity || 0);
    }
  });

  return totalQuantity;
};

/**
 * Get quantity already in cart for a specific variant size WITH specific plating
 * (Use this when you need to know about a specific plating combination)
 * @param {Array} cartProducts - Products from Redux cart
 * @param {string} productId - Product ID
 * @param {string} variationId - Variation ID
 * @param {string} variantSizeId - Variant Size ID
 * @param {string} platingId - Plating ID (optional)
 * @returns {number} - Quantity in cart
 */
export const getCartQuantityForVariantSize = (
  cartProducts,
  productId,
  variationId,
  variantSizeId,
  platingId = null
) => {
  if (!cartProducts || !productId) return 0;

  // Find product in cart
  const cartProduct = cartProducts.find(
    p => p.product_id === productId &&
         (p.product_variation_id ?? null) === (variationId ?? null)
  );

  if (!cartProduct) return 0;

  // Check if product has wholesaleSelections
  if (cartProduct.wholesaleSelections && variantSizeId) {
    const selection = cartProduct.wholesaleSelections.find(
      s => s.variantSizeId === variantSizeId &&
           (s.plating_id ?? null) === (platingId ?? null)
    );
    return selection?.qty || 0;
  }

  // For products without sizes (direct variant_size_id)
  if (cartProduct.variant_size_id === variantSizeId &&
      (cartProduct.plating_id ?? null) === (platingId ?? null)) {
    return cartProduct.quantity || 0;
  }

  return 0;
};

/**
 * Get quantity already in cart for a variant (without sizes)
 * @param {Array} cartProducts - Products from Redux cart
 * @param {string} productId - Product ID
 * @param {string} variationId - Variation ID
 * @returns {number} - Quantity in cart
 */
export const getCartQuantityForVariant = (
  cartProducts,
  productId,
  variationId
) => {
  if (!cartProducts || !productId) return 0;

  const cartProduct = cartProducts.find(
    p => p.product_id === productId &&
         (p.product_variation_id ?? null) === (variationId ?? null) &&
         !p.variant_size_id // Variant without size
  );

  return cartProduct?.quantity || 0;
};

/**
 * Get quantity already in cart for a non-variation product
 * @param {Array} cartProducts - Products from Redux cart
 * @param {string} productId - Product ID
 * @returns {number} - Quantity in cart
 */
export const getCartQuantityForProduct = (cartProducts, productId) => {
  if (!cartProducts || !productId) return 0;

  const cartProduct = cartProducts.find(
    p => p.product_id === productId && !p.product_variation_id
  );

  return cartProduct?.quantity || 0;
};

/**
 * ✅ FIX: Calculate available quantity for a variant size (stock - ALL cart quantity across platings)
 * @param {Object} variantSize - The variant size object
 * @param {Array} cartProducts - Products from Redux cart
 * @param {string} productId - Product ID
 * @param {string} variationId - Variation ID
 * @returns {number} - Available quantity
 */
export const getAvailableQuantityForVariantSize = (
  variantSize,
  cartProducts,
  productId,
  variationId
) => {
  if (!variantSize) return 0;

  const stockQuantity = variantSize.quantity || 0;
  
  // ✅ Get total quantity across ALL plating options
  const cartQuantity = getCartQuantityForVariantSizeAllPlatings(
    cartProducts,
    productId,
    variationId,
    variantSize.id
  );

  return Math.max(0, stockQuantity - cartQuantity);
};

/**
 * Calculate available quantity for a variant without sizes
 * @param {Object} variant - The variant object
 * @param {Array} cartProducts - Products from Redux cart
 * @param {string} productId - Product ID
 * @returns {number} - Available quantity
 */
export const getAvailableQuantityForVariant = (
  variant,
  cartProducts,
  productId
) => {
  if (!variant) return 0;

  const stockQuantity = variant.available_quantity || 0;
  const cartQuantity = getCartQuantityForVariant(
    cartProducts,
    productId,
    variant.id
  );

  return Math.max(0, stockQuantity - cartQuantity);
};

/**
 * Calculate available quantity for a non-variation product
 * @param {Object} product - The product object
 * @param {Array} cartProducts - Products from Redux cart
 * @returns {number} - Available quantity
 */
export const getAvailableQuantityForProduct = (product, cartProducts) => {
  if (!product) return 0;

  const stockQuantity = product.available_quantity || 0;
  const cartQuantity = getCartQuantityForProduct(
    cartProducts,
    product.id
  );

  return Math.max(0, stockQuantity - cartQuantity);
};

/**
 * ✅ FIX: Check if a variant size is fully added to cart (no more available across ALL platings)
 * @param {Object} variantSize - The variant size object
 * @param {Array} cartProducts - Products from Redux cart
 * @param {string} productId - Product ID
 * @param {string} variationId - Variation ID
 * @returns {boolean} - True if all stock is in cart
 */
export const isVariantSizeFullyInCart = (
  variantSize,
  cartProducts,
  productId,
  variationId
) => {
  const available = getAvailableQuantityForVariantSize(
    variantSize,
    cartProducts,
    productId,
    variationId
  );
  return available <= 0;
};

/**
 * Check if a variant (without sizes) is fully added to cart
 * @param {Object} variant - The variant object
 * @param {Array} cartProducts - Products from Redux cart
 * @param {string} productId - Product ID
 * @returns {boolean} - True if all stock is in cart
 */
export const isVariantFullyInCart = (
  variant,
  cartProducts,
  productId
) => {
  const available = getAvailableQuantityForVariant(
    variant,
    cartProducts,
    productId
  );
  return available <= 0;
};

/**
 * Generate quantity options based on available stock (not in cart)
 * @param {number} maxQuantity - Maximum available quantity (stock - cart)
 * @param {number} limit - Maximum options to show (default 300)
 * @returns {Array<number>} - Array of quantity options
 */
export const generateAvailableQuantityOptions = (maxQuantity, limit = 300) => {
  const max = Math.min(maxQuantity, limit);
  return Array.from({ length: max }, (_, i) => i + 1);
};